import { WIDGET_INTERACTION } from 'constants/mixpanel';
import { WidgetId } from 'types/idTypes';
import { Interaction, InteractionType, InteractionsMap, TooltipInteraction } from 'types/interactiveWidget.types';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { SUPPORTED_WIDGET_INTERACTIONS } from './InteractiveMenu.config';

/**
 * Check if the widget has any active interaction.
 *
 * @param interactions
 * @returns boolean
 */
export const hasInteraction = (interactions: InteractionsMap | undefined) => {
  if (!interactions) return false;

  if (!!interactions[InteractionType.Expandable]) return true;
  if (!!interactions[InteractionType.Tooltip]) {
    const tooltipInteraction = interactions[InteractionType.Tooltip] as TooltipInteraction;
    return !!tooltipInteraction.data?.content;
  }

  return false;
};

/**
 * Get supported interactions for a widget type.
 *
 * @param widgetType
 * @returns array of supported interactions
 */
export const getSupportedInteractions = (widgetType: WidgetType): InteractionType[] => {
  return Object.entries(SUPPORTED_WIDGET_INTERACTIONS).reduce((acc, [interactionType, supportedWidgetTypes]) => {
    if (supportedWidgetTypes.includes(widgetType)) {
      acc.push(interactionType as InteractionType);
    }
    return acc;
  }, [] as InteractionType[]);
};

/**
 * Check if the widget type supports at least one interaction.
 *
 * @param widgetId
 * @returns boolean
 */
export const checkIsWidgetTypeSupported = (widgetId: WidgetId) => {
  const widgetType = getWidgetTypeFromId(widgetId);
  return Object.values(SUPPORTED_WIDGET_INTERACTIONS).some((supportedWidgetTypes) =>
    supportedWidgetTypes.includes(widgetType),
  );
};

/**
 * Get the Mixpanel event name and props for an interactive menu event.
 *
 * @param interactionType
 * @param data
 * @param oldData
 * @returns
 */
export const getInteractiveMenuMPEvent = (
  interactionType: InteractionType,
  data: Interaction | null,
  oldData: Interaction | null,
) => {
  const deleteEventName = WIDGET_INTERACTION.Deleted.eventName;
  const createEventName = WIDGET_INTERACTION.Created.eventName;
  const editEventName = WIDGET_INTERACTION.Edited.eventName;

  switch (interactionType) {
    case InteractionType.Expandable: {
      const isDelete = !data;
      return {
        eventName: isDelete ? deleteEventName : createEventName,
        eventProps: { interaction_type: WIDGET_INTERACTION.interactionType.Expandable },
      };
    }
    case InteractionType.Tooltip: {
      const isDelete = !data || !(data as TooltipInteraction).data?.content;
      const isEdit = !!oldData && !!data && (oldData as TooltipInteraction).data?.content;
      return {
        eventName: isDelete ? deleteEventName : isEdit ? editEventName : createEventName,
        eventProps: { interaction_type: WIDGET_INTERACTION.interactionType.Label },
      };
    }
    default:
      return null;
  }
};
